import { KeyboardEvent, PointerEvent, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type Point = { x: number; y: number };
export type DockSide = 'left' | 'right';
type Dock = { side: DockSide; y: number };

/** Gap kept between the card and the window edge, so it is never flush against it or half off it. */
const EDGE = 8;
const KEY_STEP = 16;
/** While dragging, this much of the card always stays on screen, so it can be grabbed back. */
const KEEP_VISIBLE = 48;
/** Share of the card that has to be past a side edge, on release, for it to tuck into a tab there. */
const DOCK_THRESHOLD = 0.4;
/**
 * Or the pointer let go this close to a side edge. Needed because the handle sits at one end
 * of the card: dragged towards that end's edge, the pointer reaches the window edge long before
 * the card is far enough out.
 */
const DOCK_ZONE = 32;

const TAB_HEIGHT = 48;
/** The tab stays below the header and above the floating feedback/accessibility buttons. */
const TAB_TOP_RESERVE = 72;
const TAB_BOTTOM_RESERVE = 160;

const ARROWS: Record<string, Point> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

const clampY = (y: number, height: number) => Math.min(Math.max(y, EDGE), Math.max(EDGE, window.innerHeight - height - EDGE));

const clampToViewport = (point: Point, card: HTMLElement): Point => {
  const { width, height } = card.getBoundingClientRect();
  return {
    x: Math.min(Math.max(point.x, EDGE), Math.max(EDGE, window.innerWidth - width - EDGE)),
    y: clampY(point.y, height),
  };
};

/** Mid-drag the card may hang past the side edges - that is how it is tucked away - but never all the way. */
const clampWhileDragging = (point: Point, card: HTMLElement): Point => {
  const { width, height } = card.getBoundingClientRect();
  return {
    x: Math.min(Math.max(point.x, KEEP_VISIBLE - width), window.innerWidth - KEEP_VISIBLE),
    y: clampY(point.y, height),
  };
};

const clampTabY = (y: number) => Math.min(Math.max(y, TAB_TOP_RESERVE), Math.max(TAB_TOP_RESERVE, window.innerHeight - TAB_HEIGHT - TAB_BOTTOM_RESERVE));

/** Low on the left: clear of the header, and of the sidebar, which RTL puts on the right. */
const defaultDock = (): Dock => ({ side: 'left', y: clampTabY(window.innerHeight) });

/** The pointer counts only when it travelled towards that edge: a click on a handle that already sits by the edge is not a push. */
const dockSideOnRelease = (rect: DOMRect, pointerX: number, startX: number): DockSide | null => {
  const pushedLeft = pointerX <= DOCK_ZONE && pointerX < startX;
  const pushedRight = pointerX >= window.innerWidth - DOCK_ZONE && pointerX > startX;
  if (pushedLeft || -rect.left > rect.width * DOCK_THRESHOLD) return 'left';
  if (pushedRight || rect.right - window.innerWidth > rect.width * DOCK_THRESHOLD) return 'right';
  return null;
};

/**
 * Lets a position:fixed card be moved by a handle: dragged with the pointer, or nudged with
 * the arrow keys while the handle has focus (Shift for bigger steps).
 *
 * Pushed past the left or right edge - dragged more than DOCK_THRESHOLD of the way out, let go
 * within DOCK_ZONE of the edge, or nudged once more when already against it - the card tucks
 * into a small tab on that edge (`docked`), and `undock` brings it back out on the same side.
 * `tuck` puts it into a tab without a drag, and `startTucked` has it begin as one.
 * While a drag would end that way, `dockPreview` names the side, so the card can show it.
 *
 * Until the first move `position` is null and the card keeps its stylesheet placement; after
 * that the caller pins it with left/top. All of it lives in state, so it survives the card
 * being hidden and shown again for as long as the page is open.
 */
const useMovable = (cardRef: RefObject<HTMLElement | null>, { startTucked = false } = {}) => {
  const [position, setPosition] = useState<Point | null>(null);
  const [docked, setDocked] = useState<Dock | null>(() => (startTucked ? defaultDock() : null));
  const [isDragging, setIsDragging] = useState(false);
  const [dockPreview, setDockPreview] = useState<DockSide | null>(null);
  // Where on the card the pointer grabbed it, so the card does not jump to the pointer.
  const grabOffset = useRef<Point | null>(null);
  const grabStartX = useRef(0);
  // Coming out of a tab the card's size is unknown until it renders; it is clamped then.
  const clampOnRender = useRef(false);

  const moveTo = useCallback(
    (point: Point) => {
      if (cardRef.current) setPosition(clampToViewport(point, cardRef.current));
    },
    [cardRef]
  );

  const dock = (side: DockSide, rect: DOMRect) => {
    setDocked({ side, y: clampTabY(rect.top + rect.height / 2 - TAB_HEIGHT / 2) });
  };

  /**
   * Put away without a drag. A card that was moved tucks into the edge nearer to it; one still
   * in its stylesheet place goes to the default tab.
   */
  const tuck = () => {
    const card = cardRef.current;
    if (position && card) {
      const rect = card.getBoundingClientRect();
      dock(rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right', rect);
    } else {
      setDocked(defaultDock());
    }
  };

  /** Back out on the side it was tucked into, or, if it was never moved, to its stylesheet place. */
  const undock = () => {
    if (!docked) return;
    if (position) {
      clampOnRender.current = true;
      setPosition({ x: docked.side === 'left' ? EDGE : window.innerWidth, y: docked.y });
    }
    setDocked(null);
  };

  useLayoutEffect(() => {
    if (!clampOnRender.current || docked || !position || !cardRef.current) return;
    clampOnRender.current = false;
    setPosition(clampToViewport(position, cardRef.current));
  }, [docked, position, cardRef]);

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    const card = cardRef.current;
    if (!card || event.button !== 0) return;

    // No text selection while dragging, and every move reaches the handle even off its edge.
    event.preventDefault();
    const rect = card.getBoundingClientRect();
    grabOffset.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    grabStartX.current = event.clientX;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const card = cardRef.current;
    if (!grabOffset.current || !card) return;
    setPosition(clampWhileDragging({ x: event.clientX - grabOffset.current.x, y: event.clientY - grabOffset.current.y }, card));
    setDockPreview(dockSideOnRelease(card.getBoundingClientRect(), event.clientX, grabStartX.current));
  };

  const onPointerUp = (event: PointerEvent<HTMLElement>) => {
    const card = cardRef.current;
    if (grabOffset.current && card) {
      const rect = card.getBoundingClientRect();
      const side = dockSideOnRelease(rect, event.clientX, grabStartX.current);
      if (side) dock(side, rect);
      else moveTo({ x: rect.left, y: rect.top });
    }
    grabOffset.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setIsDragging(false);
    setDockPreview(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const direction = ARROWS[event.key];
    const card = cardRef.current;
    if (!direction || !card) return;

    event.preventDefault();
    const rect = card.getBoundingClientRect();
    if (position && direction.x !== 0) {
      const side: DockSide = direction.x < 0 ? 'left' : 'right';
      const isAgainstEdge = side === 'left' ? position.x <= EDGE : position.x >= window.innerWidth - rect.width - EDGE;
      if (isAgainstEdge) {
        dock(side, rect);
        return;
      }
    }
    const step = event.shiftKey ? KEY_STEP * 4 : KEY_STEP;
    moveTo({ x: rect.left + direction.x * step, y: rect.top + direction.y * step });
  };

  // A window made smaller must not strand a moved card, or a tab, out of reach.
  const isMoved = position !== null;
  const isDocked = docked !== null;
  useEffect(() => {
    if (!isMoved && !isDocked) return;

    const onResize = () => {
      setDocked((current) => current && { ...current, y: clampTabY(current.y) });
      const card = cardRef.current;
      if (card) setPosition((current) => current && clampToViewport(current, card));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMoved, isDocked, cardRef]);

  return {
    position,
    docked,
    dockPreview,
    isDragging,
    tuck,
    undock,
    handleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onKeyDown },
  };
};

export default useMovable;
